/**
 * This module handles mapping url routes to the applications tabbing mechanism
 * and is exposed as a React hook.
 * There are three types of tabs that can be opened:
 *
 * - An admin tab e.g services, tenant settings
 * - A flow canvas tab
 * - A page editor tab
 *
 * An admin tab uses the following url structure: /:tenantId/:tabName?tab=:tabKey&page_name=:pageName
 * If the tab can be opened multiple times then a tab query parameter is appended to the url to make it unique
 * and therefore trigger the Router to re-render the application. If the tab is to open a new page editor,
 * then a page name query parameter is also included.
 *
 * A flow canvas tab uses the following url structure: /:tenantId/flows/:flowId
 *
 * A page editor tab uses the following url structure: /:tenantId/pages/:pageId
 */

import { type ActionDispatch, useEffect, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { type TabAction, useTabs, useTabsDispatch } from '../components/tabs/TabsProvider';
import { tabDetailsObjects } from '../config/admin';
import { TAB_TYPES } from '../constants/tab';
import translations from '../translations';
import type { TabType } from '../types/tab';
import type { UserRole } from '../types/user';
import { generateRouteUrl, generateTabKey } from '../utils/routing';

const openTab = ({
    slug,
    tabType,
    tabKey,
    elementId,
    queryParams,
    role,
    dispatchTabs,
}: {
    slug: string | null;
    tabType: string | null;
    tabKey: string | null;
    elementId: string | null;
    queryParams: URLSearchParams;
    role: UserRole;
    tenantId: string | null;
    dispatchTabs: ActionDispatch<[action: TabAction]>;
}) => {
    const tabDetails = Object.values(tabDetailsObjects).find(({ type }) => type === tabType);

    if (!tabDetails) {
        throw new Error(`The tab named ${slug} is not recognised`);
    }

    if (!tabDetails.roles.includes(role)) {
        throw new Error(`You do not have permissions to view the ${slug} tab.`);
    }

    if (tabType === 'flow') {
        if (elementId) {
            return dispatchTabs({
                type: 'OPEN_FLOW',
                payload: { flowId: elementId },
            });
        }
    }

    if (tabType === 'page') {
        if (elementId) {
            return dispatchTabs({
                type: 'OPEN_PAGE',
                payload: { pageId: elementId, tabKey: generateTabKey() },
            });
        }
    }

    if (tabType === 'newPage') {
        return dispatchTabs({
            type: 'NEW_PAGE',
            payload: {
                title: queryParams.get('page_name') ?? 'New Page',
                tabKey: tabKey ?? generateTabKey(),
            },
        });
    }
    return dispatchTabs({
        type: 'OPEN_TAB',
        payload: { type: tabDetails.type, elementId, tabKey: tabKey ?? generateTabKey() },
    });
};

const getTabType = ({ slug, hasIdentifier }: { slug: string | null; hasIdentifier: boolean }) => {
    switch (slug) {
        case 'flows':
            return hasIdentifier ? TAB_TYPES.flow : TAB_TYPES.flows;
        case 'pages':
            return hasIdentifier ? TAB_TYPES.page : TAB_TYPES.pages;
        default:
            return Object.values(tabDetailsObjects).find((td) => td.slug === slug)?.type ?? slug;
    }
};

export const useTabRouting = (
    role: UserRole | undefined,
    environmentsIsOn: boolean,
    isAdministrator: boolean,
    onError: (error: unknown) => void,
    dispatchTabs: ActionDispatch<[action: TabAction]>,
) => {
    const [previousTenantId, setPreviousTenantId] = useState('');
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(location.search);
    const tenantId = useMatch('/:tenantId/*')?.params.tenantId ?? null;
    const slug = useMatch('/:tenantId/:slug/*')?.params.slug ?? null;
    const elementId = useMatch('/:tenantId/:slug/:elementId')?.params.elementId ?? null;

    const tabKey = queryParams.get('tab');
    const tabType = getTabType({ slug, hasIdentifier: elementId !== null });

    const onTabOpen = (openTabCallback: () => void, redirectTo: string) => {
        try {
            openTabCallback();
        } catch (error) {
            onError(error);
            navigate(`/${tenantId}/${redirectTo}`, { replace: true });
        }
    };

    useEffect(() => {
        // We need to have a reference the previous tenant ID
        // as there are conditions where hooks should not be executed
        // if the tenant ID from the previous execution does not match the current.
        if (tenantId && previousTenantId !== tenantId) {
            setPreviousTenantId(tenantId);
        }
    }, [previousTenantId, tenantId]);

    // Tab to open whenever navigating into a new tenant
    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        if (!role) {
            return;
        }

        const fallbackUrl = tabType === TAB_TYPES.page ? 'pages' : 'flows';

        if (!tabType) {
            return;
        }

        const canOpenTab = canOpenEnvironmentTab({
            environmentsIsOn,
            tabType,
            tenantId,
            redirectTo: fallbackUrl,
            isAdministrator,
        });

        if (!canOpenTab) {
            return;
        }

        onTabOpen(
            () =>
                openTab({
                    slug,
                    tabType,
                    tabKey,
                    elementId,
                    queryParams,
                    role,
                    tenantId,
                    dispatchTabs,
                }),
            fallbackUrl,
        );
    }, [tenantId]);

    // Tab to open/set as active whenever navigating to an admin tab
    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        if (!role) {
            return;
        }

        const fallbackUrl = tabType === TAB_TYPES.page ? 'pages' : 'flows';

        if (tenantId !== previousTenantId) {
            return;
        }

        const canOpenTab = canOpenEnvironmentTab({
            environmentsIsOn,
            tabType,
            tenantId,
            redirectTo: fallbackUrl,
            isAdministrator,
        });

        if (!canOpenTab) {
            return;
        }

        onTabOpen(
            () =>
                openTab({
                    slug,
                    tabType,
                    tabKey,
                    elementId,
                    queryParams,
                    role,
                    tenantId,
                    dispatchTabs,
                }),
            fallbackUrl,
        );
    }, [tabType, tabKey, elementId]);

    const canOpenEnvironmentTab = ({
        environmentsIsOn,
        tabType,
        tenantId,
        redirectTo,
        isAdministrator,
    }: {
        environmentsIsOn: boolean;
        tabType: string | null;
        tenantId: string | null;
        redirectTo: string;
        isAdministrator: boolean;
    }) => {
        if (tabType === TAB_TYPES.environments || tabType === TAB_TYPES.environment) {
            if (!environmentsIsOn) {
                onError(translations.ENVIRONMENT_setting_turned_off);
                navigate(`/${tenantId}/${redirectTo}`, { replace: true });
                return false;
            }

            if (!isAdministrator) {
                onError(translations.ENVIRONMENT_must_be_admin);
                navigate(`/${tenantId}/${redirectTo}`, { replace: true });
                return false;
            }
        }

        return true;
    };
};

export const useRouting = () => {
    const navigate = useNavigate();
    const { tabs } = useTabs();
    const dispatchTabs = useTabsDispatch();

    const reOrderTabs = (tabKey: string, index: number) => {
        dispatchTabs({ type: 'REORDER_TABS', payload: { tabKey, index } });
    };

    const getTabToActivate = (closingTabKey: string) => {
        const closingTabIndex = tabs.findIndex((tab) => tab.key === closingTabKey);

        const tabToLeft = tabs[closingTabIndex - 1];
        const tabToRight = tabs[closingTabIndex + 1];
        const tabToActivate = tabToRight ?? tabToLeft;
        return tabToActivate;
    };

    const selectTab = (tabType: TabType, tabKey: string) => {
        const elementId = tabs.find((tab) => tab.key === tabKey)?.elementId ?? null;
        openTab(tabType, tabKey, elementId);
    };

    const closeTab = (tabKey: string) => {
        const closingTab = tabs.find((tab) => tab.key === tabKey);

        if (closingTab?.type === 'flows') {
            return;
        }

        if (closingTab?.requiresCloseConfirmation) {
            openTab(closingTab.type, closingTab.key, closingTab.elementId);
        } else {
            const tabToActivate = getTabToActivate(tabKey);
            openTab(tabToActivate.type, tabToActivate.key, tabToActivate.elementId);
        }

        dispatchTabs({ type: 'CLOSE_TAB', payload: { tabKey } });
    };

    const closeTabConfirmed = (tabKey: string) => {
        const closingTab = tabs.find((tab) => tab.key === tabKey);
        const tabToActivate = getTabToActivate(tabKey);

        if (closingTab?.type === 'flows') {
            return;
        }

        openTab(tabToActivate.type, tabToActivate.key, tabToActivate.elementId);
        dispatchTabs({ type: 'CLOSE_TAB_CONFIRMED', payload: { tabKey } });
    };

    const openFlow = (flowId: string) => {
        const url = generateRouteUrl({
            tabType: TAB_TYPES.flow,
            options: { elementId: flowId },
        });
        navigate(url);
    };

    const openPage = (pageId: string) => {
        const url = generateRouteUrl({
            tabType: TAB_TYPES.page,
            options: { elementId: pageId },
        });
        navigate(url);
    };

    const newPage = (title: string) => {
        const url = generateRouteUrl({
            tabType: TAB_TYPES.newPage,
            options: { pageName: title },
        });
        navigate(url);
    };

    const openTab = (tabType: TabType, tabKey: string | null, elementId: string | null) => {
        const url = generateRouteUrl({
            tabType,
            options: { tabKey, elementId },
        });
        navigate(url);
    };

    const switchFlow = (tabKey: string, flowId: string, flowName?: string) => {
        dispatchTabs({
            type: 'SWITCH_FLOW',
            payload: {
                tabKey,
                flowId,
                flowName,
            },
        });
        const url = generateRouteUrl({
            tabType: TAB_TYPES.flow,
            options: { elementId: flowId },
        });
        navigate(url);
    };

    return {
        selectTab,
        openFlow,
        openPage,
        newPage,
        openTab,
        closeTab,
        closeTabConfirmed,
        switchFlow,
        reOrderTabs,
    };
};
